import { AzureArcStatusEnum } from '../../azure/models/azureArcStatusEnum';
import { PowerShellSessionRequestOptions } from '../../data/powershell';
import { MediaConversion, MediaConversionBase } from '../../data/units';

/**
 * Server Inventory query parameters.
 */
export interface ServerInventoryParams {
    /**
     * name of server node.
     */
    name: string;

    /**
     * options for PowerShell session request.
     */
    requestOptions?: PowerShellSessionRequestOptions;
}

/**
 * Windows Operating Systems
 */
export enum WindowsOperatingSystem {
    Unknown = 0,
    Legacy = 1,                         // XP, Server 2003, Windows NT, etc.
    WindowsVista = 2,
    Windows7 = 3,
    WindowsServer2008 = 4,
    WindowsServer2008R2 = 5,
    Windows8 = 6,
    WindowsServer2012 = 7,
    Windows8Point1 = 8,
    WindowsServer2012R2 = 9,
    Windows10 = 10,
    WindowsServer2016 = 11,
    WindowsServer2019 = 12
}

/**
 * Windows Product Types
 */
export enum WindowsProductType {
    Workstation = 1,
    DomainController = 2,
    Server = 3
}

/**
 * Smbios Info
 */
export interface SmbiosData {
    /**
     * indicator of the Serial Number assigned from Company and is accessible on exterior of device.
     */
    serialNumber: string;
    /**
     * identifies the company brand name under which the device is marketed to the end user.
     */
    manufacturer: string;
    /**
     * This  is a unique value and is intended to be associated with this specific machine.
     * This can be null depending on the machine
     */
    uuid: string;
    /**
    * identifies the baseboard and should accurately reflect
    * variances in baseboards across different devices in the same family and product name.
    * This can be null depending on the machine
    */
    baseBoardProduct: string;
    /**
    *  physical frames or structures of a machine
    */
    chassis: string[];
    /**
    * Types of Chassis defined in the specification.
    */
    chassisTypes: string[];
    /**
     * identifies the company sub-brand name, specific to a grouping of similar devices known as a product line,
     *  under which the device is marketed to end users
     */
    systemFamily: string;
    /**
     * identifies the device in a format that can be determined by Company.
     */
    systemSkuNumber: string;

    /**
    * Asset tag number of the system enclosure
    */
    smBiosAssetTag: string;
}

/**
 * The address entry for address list.
 */
export interface ServerInventoryAddressEntry {
    ipAddress: string;
    addressFamily: 'InterNetwork' | 'InterNetworkV6';
}

/**
 * Server Inventory data interface.
 */
export interface ServerInventoryData {
    /**
     * The server name.
     */
    serverName: string;

    /**
     * The name of server. (CSName/OperatingSystem)
     */
    name: string;

    /**
     * The domain name of server. (Domain/ComputerSystem)
     */
    domainName: string;

    /**
     * The workgroup name of server if any. (Workgroup/ComputerSystem)
     */
    workgroupName: string;

    /**
     * The FQDN of server if any. (ComputerSystem)
     */
    fullyQualifiedDomainName: string;

    /**
     * The FQDN of server if any. (ComputerSystem) Not lower cased.
     */
    fullyQualifiedDomainNameRaw: string;

    /**
     * The hostname of computer which was configured on the computer.
     */
    hostname: string;

    /**
     * The hostname of computer which was configured on the computer. Not lower cased.
     */
    hostnameRaw: string;

    /**
     * The NetBios name which might be trimmed by 15 characters.
     */
    netBios: string;

    /**
     * The IP address list.
     */
    addressList: ServerInventoryAddressEntry[];

    /**
     * The operating system name of server. (Caption/OperatingSystem)
     */
    operatingSystemName: string;

    /**
     * The operating system name of server. (SKU/OperatingSystem)
     */
    operatingSystemSKU: number;

    /**
     * The operating system version of server. (Version/OperatingSystem)
     */
    operatingSystemVersion: string;

    /**
     * The total size of physical memory.
     */
    totalPhysicalMemory: number;

    /**
     * The domain role.
     */
    domainRole: number;

    /**
     * The windows product type.
     */
    productType: WindowsProductType;

    /**
     * The computer manufacturer.
     */
    computerManufacturer: string;

    /**
     * The computer model.
     */
    computerModel: string;

    /**
     * The number of logical processors.
     */
    totalLogicalProcessors: number;

    /**
     * Indicating if the server has ManagementTools namespace to support TaskManager provider.
     */
    isManagementToolsAvailable: boolean;

    /**
     * Indicating if the computer has ServerManager namespace to support ServerManager provider.
     */
    isServerManagerAvailable: boolean;

    /**
     * Whether or not Windows Management Framework v5.0 or higher is installed.
     */
    isWmfInstalled: boolean;

    /**
     * Indicating if the server node is a part of Microsoft Cluster.
     */
    isCluster: boolean;

    /**
     * The FQDN of Microsoft Cluster if it's a part of Microsoft Cluster.
     */
    clusterFqdn: string;

    /**
     * Indicating if current user is a member of administrators group.
     */
    isAdministrator: boolean;

    /**
     * Indicates if the node has S2D enabled
     */
    isS2dEnabled: boolean;

    /**
    * Indicates if the node has Britannica enabled
    */
    isBritannicaEnabled: boolean;

    /**
     * Indicates if the hyper-v role is installed
     */
    isHyperVRoleInstalled: boolean;

    /**
     * Indicates if the hyper-v powershell feature is installed
     */
    isHyperVPowershellInstalled: boolean;

    /**
     * Indicates if CredSSP is enabled.
     */
    isCredSSPEnabled: boolean;

    /**
     * Indicating if remote app enabled.
     */
    isRemoteAppEnabled: boolean;

    /**
     * SMBIOS Data
     */
    smbiosData: SmbiosData;

    /**
     * The system lockdown policy enforcement mode.
     */
    systemLockdownPolicy: string;

    /**
     * Type of operating system value
     */
    osType: string;

    /**
     * Product name that a manufacturer gives to a computer
     */
    model: string;

    /**
     * Last reboot time
     */
    lastBootUpTime: string;

    /**
     * The status of the azure arc in the server
     * i.e connectioned, disconnected etc
     */
    azureArcStatus: AzureArcStatusEnum;

    /**
     * The operating system build number
     */
    buildNumber: string;

    /**
     * The operating system update build revision
     */
    updateBuildRevision: number;

    /**
     * The operating system display version eg: 22H2
     */
    operatingSystemDisplayVersion: string;

    /**
     * Indicating if the server is HCI.
     */
     isHciServer: boolean;
}

/**
 * Server Inventory class.
 */
export class ServerInventory implements ServerInventoryData {
    private static nanoDisplayFormat = '{0} Nano';
    private static server19PreviewBuildNumber = 17623;

    /*
     * OperatingSystemSKU: from https://msdn.microsoft.com/en-us/library/aa394239(v=vs.85).aspx
     *    PRODUCT_DATACENTER_NANO_SERVER (143)
     *       Windows Server Datacenter Edition (Nano Server installation)
     *    PRODUCT_STANDARD_NANO_SERVER (144)
     *       Windows Server Standard Edition (Nano Server installation)
     */
    private static nanoServerSkus: number[] = [143, 144];

    /**
     * The name of server. (CSName/OperatingSystem)
     */
    public name: string;

    /**
     * The domain name of server. (Domain/ComputerSystem)
     */
    public domainName: string;

    /**
     * The workgroup name of server if any. (Workgroup/ComputerSystem)
     */
    public workgroupName: string;

    /**
     * The FQDN of server if any. (ComputerSystem)
     */
    public fullyQualifiedDomainName: string;

    /**
     * The FQDN of server if any. (ComputerSystem) Not lower cased.
     */
    public fullyQualifiedDomainNameRaw: string;

    /**
     * The hostname of computer which was configured on the computer.
     */
    public hostname: string;

    /**
     * The hostname of computer which was configured on the computer. Not lower cased.
     */
    public hostnameRaw: string;

    /**
     * The NetBios name which might be trimmed by 15 characters.
     */
    public netBios: string;

    /**
     * The IP address list.
     */
    public addressList: ServerInventoryAddressEntry[];

    /**
     * The operating system name of server. (Caption/OperatingSystem)
     */
    public operatingSystemName: string;

    /**
     * The operating system name of server. (SKU/OperatingSystem)
     */
    public operatingSystemSKU: number;

    /**
     * The operating system version of server. (Version/OperatingSystem)
     */
    public operatingSystemVersion: string;

    /**
     * The total size of physical memory.
     */
    public totalPhysicalMemory: number;

    /**
     * The domain role.
     */
    public domainRole: number;

    /**
     * The windows product type.
     */
    public productType: WindowsProductType;

    /**
     * The computer manufacturer.
     */
    public computerManufacturer: string;

    /**
     * The computer model.
     */
    public computerModel: string;

    /**
     * The number of logical processors.
     */
    public totalLogicalProcessors: number;

    /**
     * Indicating if the server has ManagementTools namespace to support TaskManager provider.
     */
    public isManagementToolsAvailable: boolean;

    /**
     * Indicating if the computer has ServerManager namespace to support ServerManager provider.
     */
    public isServerManagerAvailable: boolean;

    /**
     * Whether or not Windows Management Framework v5.0 or higher is installed.
     */
    public isWmfInstalled: boolean;

    /**
     * Indicating if the server node is a part of Microsoft Cluster.
     */
    public isCluster: boolean;

    /**
     * The FQDN of Microsoft Cluster if it's a part of Microsoft Cluster.
     */
    public clusterFqdn: string;

    /**
     * Indicating if current user is a member of administrators group.
     */
    public isAdministrator: boolean;

    /**
     * Indicates if the node has S2D enabled
     */
    public isS2dEnabled: boolean;

    /**
     * Indicates if the node has Britannica enabled
     */
    public isBritannicaEnabled: boolean;

    /**
     * Indicates if the hyper-v role is installed
     */
    public isHyperVRoleInstalled: boolean;

    /**
     * Indicates if the hyper-v powershell feature is installed
     */
    public isHyperVPowershellInstalled: boolean;

    /**
     * Indicates if CredSSP is enabled.
     */
    public isCredSSPEnabled: boolean;

    /**
     * Indicating if remote app enabled.
     */
    public isRemoteAppEnabled: boolean;

    /**
     * SMBIOS Data
     */
    public smbiosData: SmbiosData;

    /**
     * The system lockdown policy enforcement mode.
     */
    public systemLockdownPolicy: string;

    /**
     * Type of operating system value
     */
    public osType: string;

    /**
     * Product name that a manufacturer gives to a computer
     */
    public model: string;

    /**
     * Last reboot time
     */
    public lastBootUpTime: string;

    /**
     * The status of the azure arc in the server
     * i.e connectioned, disconnected etc
     */
    azureArcStatus: AzureArcStatusEnum;

    /*
     * Operating system serial number
     */
    osSerialNumber: string;

    /**
     * The operating system build number
     */
    public buildNumber: string;

    /**
     * The operating system update build revision
     */
    public updateBuildRevision: number;

    /**
     * The operating system display version eg: 22H2
     */
    public operatingSystemDisplayVersion: string;

    /**
     * Indicating if the server is HCI.
     */
    public isHciServer: boolean;

    /**
     * Initializes a new instance of the ServerInventory Class.
     *
     * @param serverName the server name to query.
     * @param data the server inventory recovered data.
     */
    constructor(public serverName: string, data?: ServerInventoryData) {
        if (data) {
            Object.assign(this, data);
        }
    }

    /**
     * Gets the sku number indicating whether the computer is Nano server.
     *
     * @return boolean true if nano
     */
    public get isNano(): boolean {
        // given sku number is in nanoServerSkus array?
        return ServerInventory.nanoServerSkus.indexOf(this.operatingSystemSKU) > -1;
    }

    /**
     * Gets a value indicating if this is a windows server
     *
     * @return boolean true if this is a server
     */
    public get isServer(): boolean {
        return this.productType === WindowsProductType.Server;
    }

    /**
     * Gets a value indicating if this is a windows client
     *
     * @return boolean true if this is a client
     */
    public get isClient(): boolean {
        return this.productType === WindowsProductType.Workstation;
    }

    /**
     * Indicates whether the computer is domain controller or not.
     *
     * @return boolean true if domain controller
     */
    public get isDomainController(): boolean {
        // As per https://msdn.microsoft.com/en-us/library/windows/desktop/aa394102(v=vs.85)
        // domainRole of 4 or 5 means it's a domain controller.
        return (this.domainRole === 4 || this.domainRole === 5);
    }

    /**
     * Gets the display name of operating system.
     */
    public get operatingSystemDisplayName(): string {
        return this.isNano ? ServerInventory.nanoDisplayFormat.format(this.operatingSystemName) : this.operatingSystemName;
    }

    /**
     * Gets the display name of total physical memory.
     */
    public get totalPhysicalMemoryDisplayName(): string {
        return MediaConversion.getConvertedValue(this.totalPhysicalMemory, MediaConversionBase.Binary);
    }

    /**
     * Gets the operating system enumb of the machine
     * see: https://msdn.microsoft.com/en-us/library/windows/desktop/ms724832(v=vs.85).aspx for operating system version mapping.
     */
    public get operatingSystem(): WindowsOperatingSystem {

        if (!this.operatingSystemVersion) {
            return WindowsOperatingSystem.Unknown;
        }

        // windows 10, server 2016, and server 2019
        // report anything newer as one of these options.
        if (this.operatingSystemVersion.indexOf('.') > 1) {
            // since . is more than 2 digits from the start of the screen we are going to assume its '10.', '11.', etc...
            if (this.isServer) {
                if (+this.operatingSystemVersion.split('.').last() >= ServerInventory.server19PreviewBuildNumber) {
                    // 17623 first insiders preview of 2019
                    return WindowsOperatingSystem.WindowsServer2019;
                } else {
                    // 14393 official release version of 2016
                    // fall back to 2016 for versions between 14393 and 17623
                    return WindowsOperatingSystem.WindowsServer2016;
                }
            } else {
                return WindowsOperatingSystem.Windows10;
            }
        }

        // windows 8.1 and server 2012r2
        if (this.operatingSystemVersion.startsWith('6.3')) {
            return this.isServer
                ? WindowsOperatingSystem.WindowsServer2012R2
                : WindowsOperatingSystem.Windows8Point1;
        }

        // windows 8 and server 2012
        if (this.operatingSystemVersion.startsWith('6.2')) {
            return this.isServer
                ? WindowsOperatingSystem.WindowsServer2012
                : WindowsOperatingSystem.Windows8;
        }

        // windows 7 and server 2008 R2
        if (this.operatingSystemVersion.startsWith('6.1')) {
            return this.isServer
                ? WindowsOperatingSystem.WindowsServer2008R2
                : WindowsOperatingSystem.Windows7;
        }

        // windows vista and server 2008
        if (this.operatingSystemVersion.startsWith('6.0')) {
            return this.isServer
                ? WindowsOperatingSystem.WindowsServer2008
                : WindowsOperatingSystem.WindowsVista;
        }

        // assume a legacy os that somehow was able to run powershell.(unlikely)
        return WindowsOperatingSystem.Legacy;
    }
}
